import React, { useState, useEffect } from 'react'
import { authFetch } from '../api/authFetch'
import InlineAlert from './InlineAlert'

export default function ChatSlotsPanel({ chatRowId }) {
    const [slots, setSlots] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        let cancelled = false

        const load = async () => {
            setLoading(true)
            setError(null)
            try {
                const res = await authFetch(`/api/chats/${chatRowId}/slots`)
                const data = await res.json()
                if (!res.ok) {
                    throw new Error(data.error || 'Failed to load slots')
                }
                if (!cancelled) {
                    setSlots(data.slots || [])
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e.message || 'Failed to load slots')
                    setSlots([])
                }
            } finally {
                if (!cancelled) {
                    setLoading(false)
                }
            }
        }

        load()
        return () => {
            cancelled = true
        }
    }, [chatRowId])

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
            </div>
        )
    }

    if (error) {
        return (
            <InlineAlert variant="error" className="mb-0">
                <p>{error}</p>
            </InlineAlert>
        )
    }

    if (slots.length === 0) {
        return (
            <p className="text-sm text-gray-500 py-2">No slots for this user.</p>
        )
    }

    const cellWrap = 'align-top break-all'

    return (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full table-fixed text-sm">
                <thead className="bg-gray-100 border-b border-gray-200">
                    <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase w-[12%]">
                            Name
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase w-[18%]">
                            Title
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase w-[18%]">
                            Search
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase w-[18%]">
                            Location
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase w-[10%]">
                            Mode
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase w-[10%]">
                            On/Off
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase w-[6%]">
                            Pending
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase w-[6%]">
                            Sent
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {slots.map((slot) => (
                        <tr
                            key={slot.id}
                            className={
                                slot.isDeleted
                                    ? 'bg-red-50 hover:bg-red-100/90'
                                    : 'hover:bg-gray-50'
                            }
                        >
                            <td className={`px-3 py-2 font-medium text-gray-900 ${cellWrap}`}>{slot.name}</td>
                            <td className={`px-3 py-2 text-gray-700 ${cellWrap}`}>{slot.title || '—'}</td>
                            <td className={`px-3 py-2 text-gray-600 ${cellWrap}`}>{slot.search || '—'}</td>
                            <td className={`px-3 py-2 text-gray-600 ${cellWrap}`}>
                                {slot.remoteOnly ? 'Remote' : slot.location || '—'}
                            </td>
                            <td className={`px-3 py-2 text-gray-700 ${cellWrap}`}>{slot.mode}</td>
                            <td className={`px-3 py-2 ${cellWrap}`}>
                                <span
                                    className={
                                        slot.isActive
                                            ? 'inline-block rounded px-2 py-0.5 text-xs font-medium bg-green-600 text-white'
                                            : 'inline-block rounded px-2 py-0.5 text-xs font-medium bg-amber-700 text-white'
                                    }
                                >
                                    {slot.isActive ? 'On' : 'Off'}
                                </span>
                            </td>
                            <td className="px-3 py-2 text-right align-top tabular-nums text-amber-700 font-medium">
                                {slot.notificationsPending}
                            </td>
                            <td className="px-3 py-2 text-right align-top tabular-nums text-green-700 font-medium">
                                {slot.notificationsSent}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
